import { FC, useState, createContext, ReactNode, useContext, useCallback } from 'react'
import { BodyPortal } from 'components'

interface ModalData {
  id: string
  component: ReactNode
  stack?: string
  stackIndex?: number
}

export type ContextData = {
  modal: ModalData[]
  isSocketConnected: boolean
}

export type AppContext = {
  setModal: (content: ModalData) => void
  setCloseModal: (id: string) => void
  resetModals: () => void
  setSocketConnected: (value: boolean) => void
  data: ContextData
  setModals: (data: ContextData['modal']) => void
}

interface Props {
  initialValue?: Partial<ContextData>
  children: ReactNode
}

const emptyState: ContextData = {
  modal: [],
  isSocketConnected: false,
}

const Context = createContext<AppContext | null>(null)

const AppContextProvider: FC<Props> = ({ initialValue, children }) => {
  const [data, setData] = useState<ContextData>({
    ...emptyState,
    ...initialValue,
  })

  const isTopModalInStack = (currentModal: ModalData): boolean => {
    const stackModals = data.modal.filter((item) => item.stack === currentModal.stack)
    const maxIndex = stackModals.reduce(
      (max, item) => (typeof item.stackIndex === 'number' ? Math.max(max, item.stackIndex) : max),
      -1,
    )

    return currentModal.stackIndex === maxIndex
  }

  return (
    <Context.Provider
      value={{
        data,
        setModals: useCallback((value) => {
          setData((prev) => ({ ...prev, modal: value }))
        }, []),
        setModal: useCallback((value) => {
          setData((prev) => {
            // replace existing modal
            const existingModalID = prev.modal.findIndex((item) => item.id === value.id)
            if (existingModalID >= 0) {
              return {
                ...prev,
                modal: prev.modal.map((item) => {
                  if (item.id === value.id) {
                    return { ...item, ...value }
                  }
                  return item
                }),
              }
            }

            // if stack specified, add new one
            if (value.stack) {
              // find max index in stack
              const stackModals = prev.modal.filter((item) => item.stack === value.stack)
              const maxIndex = stackModals.reduce(
                (max, item) => (typeof item.stackIndex === 'number' ? Math.max(max, item.stackIndex) : max),
                -1,
              )
              const stackIndex = maxIndex + 1

              return {
                ...prev,
                modal: [...prev.modal, { ...value, stackIndex }],
              }
            }

            // if no stack, add new one
            return { ...prev, modal: [...prev.modal, value] }
          })
        }, []),
        setCloseModal: useCallback((id) => {
          setData((prev) => ({ ...prev, modal: prev.modal.filter((item) => item.id !== id) }))
        }, []),
        setSocketConnected: useCallback((value) => {
          setData((prev) => ({ ...prev, isSocketConnected: value }))
        }, []),
        resetModals: useCallback(() => {
          setData((prev) => ({ ...prev, modal: [] }))
        }, []),
      }}
    >
      {children}
      {data.modal.map((item) => {
        const hiddenByStack = item.stack ? !isTopModalInStack(item) : false
        return (
          <BodyPortal key={item.id}>
            <div style={{ display: hiddenByStack ? 'none' : undefined }}>{item.component}</div>
          </BodyPortal>
        )
      })}
    </Context.Provider>
  )
}

export const useAppContext = () => {
  const context = useContext(Context)

  if (context == null) {
    throw new Error('useAppContext was used outside of its Provider')
  }

  return context
}

export default AppContextProvider
